package one.healthium.health_log.ui

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.defaultMinSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.navigationBarsPadding
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import loco.HEALTH_LOG_SAVE_BUTTON
import loco.HEALTH_LOG_SCREEN_TITLE
import one.healthium.domain.health_log.models.HealthLogAnswerModel
import one.healthium.domain.health_log.models.HealthLogQuestionModel
import one.healthium.health_log.ui.components.HealthLogQuestionItem
import one.healthium.health_log.ui.models.HealthLogQuestionUiModel
import one.healthium.ui.common.event_producer.EventEffect
import one.healthium.ui.common.message.AlertUiMessageHandler
import one.healthium.ui.compose.components.AppProgressButton
import one.healthium.ui.compose.components.AppScaffold
import one.healthium.ui.compose.components.AppSheetTopBar
import one.healthium.ui.compose.theme.AppTheme
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

interface HealthLogEventDelegate {
    fun onCloseClicked()
}

@Composable
fun HealthLogScreen(eventDelegate: HealthLogEventDelegate, taskId: String?) {
    val viewModel = koinViewModel<HealthLogViewModel>()
    val state by viewModel.state.collectAsState()

    EventEffect(viewModel.events) { event ->
        when (event) {
            HealthLogEvent.OnHealthLogSaved -> eventDelegate.onCloseClicked()
        }
    }
    AlertUiMessageHandler(viewModel.uiMessage)

    HealthLogScreenContent(
        state = state,
        onCloseClicked = eventDelegate::onCloseClicked,
        onSaveClicked = { viewModel.onSaveClicked(taskId) },
        onOptionSelected = viewModel::onOptionSelected,
    )
}

@Composable
private fun HealthLogScreenContent(
    state: HealthLogUiState,
    onCloseClicked: () -> Unit,
    onOptionSelected: (HealthLogQuestionUiModel, HealthLogAnswerModel) -> Unit,
    onSaveClicked: () -> Unit,
) {
    AppScaffold(
        topBar = {
            AppSheetTopBar(
                onCloseClicked = onCloseClicked,
            )
        },
    ) {
        Box {
            Column(
                modifier = Modifier
                    .padding(it)
                    .fillMaxWidth()
                    .verticalScroll(rememberScrollState())
                    .navigationBarsPadding(),
                horizontalAlignment = Alignment.CenterHorizontally,
            ) {
                Spacer(Modifier.height(14.dp))
                Text(
                    modifier = Modifier.padding(horizontal = 32.dp),
                    text = stringResource(loco.Res.string.HEALTH_LOG_SCREEN_TITLE),
                    style = AppTheme.logControlStyle?.titleStyle ?: AppTheme.textStyles.secondary_30_500,
                    color = AppTheme.colors.text100,
                    textAlign = TextAlign.Center,
                )
                Spacer(Modifier.height(12.dp))
                state.uiData?.forEach { question ->
                    HealthLogQuestionItem(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(horizontal = 24.dp, vertical = 12.dp),
                        model = question,
                        onOptionSelected = { option ->
                            onOptionSelected(question, option)
                        },
                    )
                }
                Spacer(Modifier.height(150.dp))
            }
            AppProgressButton(
                modifier = Modifier
                    .defaultMinSize(minWidth = 200.dp)
                    .padding(bottom = 50.dp)
                    .navigationBarsPadding()
                    .align(Alignment.BottomCenter),
                onClick = onSaveClicked,
                loading = state.saveInProgress,
                text = stringResource(loco.Res.string.HEALTH_LOG_SAVE_BUTTON),
            )
        }
    }
}

@Composable
@Preview(showBackground = true)
fun HealthLogScreenPreview() {
    AppTheme {
        HealthLogScreenContent(
            state = HealthLogUiState(
                questions = listOf(HealthLogQuestionModel.preview),
                selectedAnswers = mapOf(
                    HealthLogQuestionModel.preview.id to HealthLogQuestionModel.preview.answers.first()
                )
            ),
            onCloseClicked = {},
            onSaveClicked = {},
            onOptionSelected = { _, _ -> }
        )
    }
}
